import type { SearchType } from '@lt/api/dist/types/searchParams';
import { DTOError } from 'src/error/errors/DTOError';

import { initialState } from './constants';
import type { PackageStateType } from './types';

import { availabilityDTO } from './availabilityDTO';
import { cancellationPolicyDTO } from './cancellationPolicyDTO';
import { datesInfoDTO, dateYMDStringDTO } from './datesInfoDTO';
import { extrasDTO } from './extrasDTO';
import { kidsAgesDTO } from './kidsAgesDTO';
import { labelsDTO } from './labelsDTO';
import { noticesDTO } from './noticesDTO';
import { operatorDTO } from './operatorDTO';
import { miscDataDTO } from './miscDataDTO';
import { transferInfoDTO } from './transferInfoDTO';

export const packageDTO = (data: unknown): PackageStateType => {
  if (data && typeof data === 'object') {
    if (
      !('id' in data) ||
      !('net_price' in data) ||
      !('tour_id' in data) ||
      !('kids_count' in data) ||
      !('pansion' in data) ||
      !('pansion_description' in data) ||
      !('region_name' in data) ||
      !('request_id' in data)
    ) {
      throw new DTOError('PackageDTO', data);
    }

    return {
      id:
        'id' in data && typeof data.id === 'number' ? data.id : initialState.id,
      availability:
        'availability' in data ? availabilityDTO(data.availability) : null,
      extras: 'extras' in data ? extrasDTO(data.extras) : [],
      price:
        'price' in data &&
        (typeof data.price === 'number' || typeof data.price === 'string')
          ? Number(data.price)
          : initialState.price,
      fuel_charge:
        'fuel_charge' in data &&
        (typeof data.fuel_charge === 'number' ||
          typeof data.fuel_charge === 'string')
          ? Number(data.fuel_charge)
          : initialState.fuel_charge,
      net_price:
        'net_price' in data &&
        (typeof data.net_price === 'number' ||
          typeof data.net_price === 'string')
          ? Number(data.net_price)
          : initialState.net_price,
      transfer:
        'transfer' in data && typeof data.transfer === 'boolean'
          ? data.transfer
          : false,
      medical_insurance:
        'medical_insurance' in data &&
        typeof data.medical_insurance === 'boolean'
          ? data.medical_insurance
          : false,
      previous_price:
        'previous_price' in data && typeof data.previous_price === 'string'
          ? data.previous_price
          : initialState.previous_price,
      misc_data: 'misc_data' in data ? miscDataDTO(data.misc_data) : null,
      instant_confirm:
        'instant_confirm' in data && typeof data.instant_confirm === 'boolean'
          ? data.instant_confirm
          : false,
      labels: 'labels' in data ? labelsDTO(data.labels) : [],
      cancellation_policy:
        'cancellation_policy' in data
          ? cancellationPolicyDTO(data.cancellation_policy)
          : null,
      confirmability_index:
        'confirmability_index' in data &&
        typeof data.confirmability_index === 'number'
          ? data.confirmability_index
          : null,
      tour_id:
        'tour_id' in data && typeof data.tour_id === 'string'
          ? data.tour_id
          : initialState.tour_id,
      dates_info: 'dates_info' in data ? datesInfoDTO(data.dates_info) : null,
      kids_count:
        'kids_count' in data && typeof data.kids_count === 'number'
          ? data.kids_count
          : initialState.kids_count,
      kids_ages: 'kids_ages' in data ? kidsAgesDTO(data.kids_ages) : [],
      adults_count:
        'adults_count' in data && typeof data.adults_count === 'number'
          ? data.adults_count
          : 0,
      pansion:
        'pansion' in data && typeof data.pansion === 'string'
          ? data.pansion
          : initialState.pansion,
      pansion_original:
        'pansion_original' in data && typeof data.pansion_original === 'string'
          ? data.pansion_original
          : initialState.pansion_original,
      pansion_description:
        'pansion_description' in data &&
        typeof data.pansion_description === 'string'
          ? data.pansion_description
          : initialState.pansion_description,
      operator: 'operator' in data ? operatorDTO(data.operator) : null,
      booking_url:
        'booking_url' in data && typeof data.booking_url === 'string'
          ? data.booking_url
          : null,
      surcharge:
        'surcharge' in data && typeof data.surcharge === 'string'
          ? data.surcharge
          : null,
      region_name:
        'region_name' in data && typeof data.region_name === 'string'
          ? data.region_name
          : initialState.region_name,
      operator_id:
        'operator_id' in data && typeof data.operator_id === 'number'
          ? data.operator_id
          : 0,
      hotel_id:
        'hotel_id' in data && typeof data.hotel_id === 'number'
          ? data.hotel_id
          : null,
      request_id:
        'request_id' in data && typeof data.request_id === 'string'
          ? data.request_id
          : initialState.request_id,
      search_request:
        'search_request' in data && typeof data.search_request === 'string'
          ? data.search_request
          : null,
      notices: 'notices' in data ? noticesDTO(data.notices) : [],
      departure_id:
        'departure_id' in data && typeof data.departure_id === 'number'
          ? data.departure_id
          : null,
      manual:
        'manual' in data && typeof data.manual === 'boolean'
          ? data.manual
          : false,
      original_room:
        'original_room' in data && typeof data.original_room === 'string'
          ? data.original_room
          : null,
      archived_at:
        'archived_at' in data && typeof data.archived_at === 'string'
          ? data.archived_at
          : null,
      hot: 'hot' in data && typeof data.hot === 'boolean' ? data.hot : false,
      arrival_date:
        'arrival_date' in data
          ? dateYMDStringDTO(data.arrival_date)
          : initialState.arrival_date,
      room_type:
        'room_type' in data && typeof data.room_type === 'string'
          ? data.room_type
          : initialState.room_type,
      search_type:
        'search_type' in data && typeof data.search_type === 'string'
          ? (data.search_type as SearchType)
          : initialState.search_type,
      nights_count:
        'nights_count' in data && typeof data.nights_count === 'number'
          ? data.nights_count
          : initialState.nights_count,
      start_date:
        'start_date' in data
          ? dateYMDStringDTO(data.start_date)
          : initialState.start_date,
      transfer_info:
        'transfer_info' in data ? transferInfoDTO(data.transfer_info) : null,
    };
  }

  return initialState;
};
